import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Linkedin, Mail, ArrowRight } from "lucide-react";

const teamMembers = [
  {
    id: 1,
    name: "CA Manish Gupta",
    role: "Chartered Accountant | Startup Advisor & Virtual CFO",
    bio: "CA Manish Gupta is a Chartered Accountant with over 16 years of professional experience in Startup Valuation, CFO Services, Regulatory Compliances and Taxation Advisory.",
    expertise: ["ESOP & F&O Taxation Advisory", "Ideal Fund Management", "Startup Valuation & Funding"],
    image: "/finease-x/CA Manish Gupta.jpeg",
    linkedin: "https://linkedin.com",
    email: "manish@fineaseconsulting.com",
  },
  {
    id: 2,
    name: "CA Abhishek G N",
    role: "Chartered Accountant | Startup & Business Advisory Specialist",
    bio: "CA Abhishek G N is a Chartered Accountant with over 13 years of professional experience in finance, taxation, and regulatory compliance.",
    expertise: ["Accounting & Financial Management", "Taxation & Regulatory Compliance", "Startup & Business Advisory"],
    image: "/finease-x/CA Abhishek GN.jpeg",
    linkedin: "https://linkedin.com",
    email: "abhishek@fineaseconsulting.com",
  },
  {
    id: 3,
    name: "CS Garima Goyal",
    role: "Company Secretary | Corporate Law & Compliance Expert",
    bio: "CS Garima Goyal is a qualified Company Secretary with extensive experience in corporate law, secretarial compliance, and SEBI regulations.",
    expertise: ["Company Incorporation", "ROC Compliance", "Corporate Governance"],
    image: "/finease-x/CS Garima Goyal.jpeg",
    linkedin: "https://linkedin.com",
    email: "garima@fineaseconsulting.com",
  },
  {
    id: 4,
    name: "CS Pooja Kansal",
    role: "Company Secretary | Secretarial & Legal Specialist",
    bio: "CS Pooja Kansal is a qualified Company Secretary with expertise in legal documentation, contract drafting, and corporate restructuring.",
    expertise: ["Legal Documentation", "Contract Drafting", "M&A Due Diligence"],
    image: "/finease-x/CS Pooja Kansal.jpeg",
    linkedin: "https://linkedin.com",
    email: "pooja@fineaseconsulting.com",
  },
];

export const TeamSection = () => {
  return (
    <section data-testid="team-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16"
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Our Experts
          </span>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Meet Our Team
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            A team of qualified Chartered Accountants and Company Secretaries dedicated to helping businesses 
            navigate complex financial and regulatory landscapes with confidence.
          </p>
        </motion.div>

        {/* Team Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-[#121212] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00C896]/20 transition-all duration-300"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Image */}
                <div className="relative w-full sm:w-40 md:w-48 aspect-square sm:aspect-auto overflow-hidden shrink-0">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                {/* Info */}
                <div className="p-5 sm:p-6 flex flex-col justify-center flex-1">
                  <h3
                    className="text-lg md:text-xl font-semibold text-white mb-1"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {member.name}
                  </h3>
                  <p className="text-xs text-[#00C896] mb-3 line-clamp-1">{member.role}</p>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4 line-clamp-2">
                    {member.bio}
                  </p>
                  
                  {/* Expertise Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {member.expertise.slice(0, 2).map((skill, i) => (
                      <span
                        key={i}
                        className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Social Links */}
                  <div className="flex gap-2">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/30 transition-colors"
                    >
                      <Linkedin size={14} />
                    </a>
                    <a
                      href={"mailto:" + member.email}
                      className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/30 transition-colors"
                    >
                      <Mail size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Team Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <Link
            to="/team"
            className="inline-flex items-center gap-2 text-[#00C896] hover:text-[#00C896]/80 transition-colors group"
          >
            <span className="text-sm font-medium">View Full Team Profiles</span>
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;
